import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import staticsService from "api/statics/statics.service";
import { useAppDispatch } from "store";
import { markSitePreviewExpired, setSiteImages, setSitePreview } from "store/siteImagesSlice";

/** The query param backoffice's "Taslakları önizle" link carries. Also spelled in public/index.html's bootstrap. */
export const SITE_PREVIEW_PARAM = "site_preview";
/** Per TAB: a preview link opened in one tab never leaks drafts into another, and closing the tab ends it. */
export const SITE_PREVIEW_STORAGE_KEY = "refabric.sitePreviewToken";

declare global {
  interface Window {
    /** Set by public/index.html's site-preview bootstrap, which takes the token out of the URL before any tracker runs. */
    __RF_SITE_PREVIEW__?: string;
  }
}

const readPreviewToken = (): string | null => {
  try {
    const stored = sessionStorage.getItem(SITE_PREVIEW_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Storage blocked (private window): the bootstrap kept it in memory for this page load.
  }

  return window.__RF_SITE_PREVIEW__ || null;
};

export const writePreviewToken = (token: string | null) => {
  if (token) window.__RF_SITE_PREVIEW__ = token;
  else delete window.__RF_SITE_PREVIEW__;
  try {
    if (token) sessionStorage.setItem(SITE_PREVIEW_STORAGE_KEY, token);
    else sessionStorage.removeItem(SITE_PREVIEW_STORAGE_KEY);
  } catch {
    // Storage blocked (private window): memory carries it for this page load.
  }
};

/**
 * Loads the backoffice "Site Görselleri" slots once per app load. Mounted above `<Routes>` so it also
 * runs on the signed-out login screen. Every failure keeps the bundled assets — nothing on screen
 * depends on this request.
 */
const SiteImagesBoot = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkToken = searchParams.get(SITE_PREVIEW_PARAM);
  const token = linkToken || readPreviewToken();

  // Backstop for a preview url reached WITHOUT a full page load (the bootstrap in public/index.html handles the normal
  // case before any tracker runs): keep the token for this tab, then take it out of the address bar.
  useEffect(() => {
    if (!linkToken) return;
    writePreviewToken(linkToken);
    const next = new URLSearchParams(searchParams);
    next.delete(SITE_PREVIEW_PARAM);
    setSearchParams(next, { replace: true });
  }, [linkToken]);

  useEffect(() => {
    let cancelled = false;
    const loadPublished = () =>
      staticsService
        .getSiteImages()
        .then((res) => {
          if (cancelled || res?._handled) return;
          dispatch(setSiteImages(res?.responseData?.images ?? []));
        })
        .catch(() => {
          // Network / 5xx: keep the bundled assets.
        });

    if (!token) {
      loadPublished();

      return () => {
        cancelled = true;
      };
    }

    staticsService
      .getSiteImagesPreview(token)
      .then((res) => {
        if (cancelled) return;
        const data = res?.responseData;
        if (res?._handled || !data) return loadPublished();
        dispatch(setSitePreview({ images: data.images, loginImages: data.login_images, expiresAt: data.expires_at }));
      })
      .catch((err) => {
        if (cancelled) return;
        // HttpClient exempts this path from its logout on BOTH, so both mean "this link no longer opens".
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          writePreviewToken(null);
          dispatch(markSitePreviewExpired());
        }
        loadPublished();
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return null;
};

export default SiteImagesBoot;
